/**
 * apps/automations-portal/src/features/admin/ClientsPage.tsx
 *
 * Admin landing page: every client, their status, care plan and how many
 * automations are deployed. "New client" opens a dialog that posts to the
 * real, implemented backend route.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from '@/components/ui/sonner';
import { adminApi } from '@/lib/api';
import { CARE_PLAN_LABEL } from '@/lib/types';
import type { Client, ClientStatus } from '@/lib/types';

const STATUS_VARIANT: Record<ClientStatus, BadgeProps['variant']> = {
  ACTIVE: 'success',
  PAUSED: 'warning',
  OFFBOARDED: 'outline',
};


export function ClientsPage() {
  const [clients, setClients] = useState<Client[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  function load() {
    setError(null);
    adminApi
      .listClients()
      .then(setClients)
      .catch(() => setError('Could not load clients. Try refreshing the page.'));
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6 p-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Clients</h1>
          <p className="text-sm text-muted-foreground">Every business using Hattie Automations.</p>
        </div>
        <NewClientDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onCreated={(client) => {
            setClients((prev) => (prev ? [client, ...prev] : [client]));
          }}
        />
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {!error && !clients && (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      )}

      {clients?.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          No clients yet. Add the first one to get started.
        </div>
      )}

      {clients && clients.length > 0 && (
        <div className="rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Care plan</TableHead>
                <TableHead className="text-right">Deployments</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.map((client) => (
                <TableRow key={client.id}>
                  <TableCell>
                    <Link to={`/admin/clients/${client.id}`} className="font-medium hover:underline">
                      {client.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Badge variant={STATUS_VARIANT[client.status]}>{client.status}</Badge>
                  </TableCell>
                  <TableCell>{CARE_PLAN_LABEL[client.carePlan]}</TableCell>
                  <TableCell className="text-right">{client.deploymentCount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function NewClientDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (client: Client) => void;
}) {
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const client = await adminApi.createClient({ name: name.trim() });
      onCreated(client);
      toast('Client added', { description: `${client.name} is on the client list.` });
      setName('');
      onOpenChange(false);
    } catch {
      setError('Could not create that client. Try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button type="button">
          <Plus className="h-4 w-4" />
          New client
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New client</DialogTitle>
          <DialogDescription>Add a business to Hattie Automations.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-client-name">Business name</Label>
            <Input
              id="new-client-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Riverside Dental"
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Adding…' : 'Add client'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
